import "../style/MainPage.scss";
import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "@shared/First";
import { getProducts } from "@shared/First";
import EditBalance from "@features/EditBalance";
import { OpenButton, SearchButton } from "@features/Filter";
import Reload from "@features/Reload";
import SearchBar from "@features/SearchBar";
import FilterWrapper from "@widgets/FilterWrapper";
import Header from "@widgets/Header";
import ProductWrapper from "@widgets/ProductWrapper";
import { fetchWarehouses } from "../api/getWarehouse";
import { io } from "socket.io-client";
import _ from "lodash";

/** Достаёт человеческий текст ошибки из ответа сервера, если он там есть. */
function readServerMessage(responseText) {
  try {
    const message = JSON.parse(responseText)?.message;
    return typeof message === "string" && message.trim() ? message : null;
  } catch {
    return null;
  }
}

const MainPage = () => {
  const isMobile = useMediaQuery("(max-width: 800px)");

  const [isModalOpen, setModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isEditingStocks, setIsEditingStocks] = useState(false);
  const [isSavingStocks, setIsSavingStocks] = useState(false);


  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [productsFromDB, setProductsFromDB] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [user, setUser] = useState(null);

  const socketRef = useRef(null);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const page = parseInt(queryParams.get("page") || currentPage, 10);
  const limit = parseInt(queryParams.get("limit") || 100, 10);

  // фронтовое скрытие архива
  const showArchived = queryParams.get("is_archived") === "true";

  // === helpers для скролла ===
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const scrollToBottom = useCallback(() => {
    const doc = document.documentElement;
    window.scrollTo({ top: doc.scrollHeight, behavior: "smooth" });
  }, []);

  // состояние для плавающей кнопки: мы у верха?
  const [atTop, setAtTop] = useState(true);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      setAtTop(y < 100);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (isSavingStocks) {
      document.body.classList.add('is-saving-stocks');
    } else {
      document.body.classList.remove('is-saving-stocks');
    }
    return () => document.body.classList.remove('is-saving-stocks');
  }, [isSavingStocks]);

  const bodyLockRef = useRef(null);

  useEffect(() => {
    const body = document.body;
    const docEl = document.documentElement;

    if (isModalOpen) {
      // запомним исходные стили, чтобы потом точно вернуть
      bodyLockRef.current = {
        overflow: body.style.overflow,
        paddingRight: body.style.paddingRight,
        position: body.style.position,
        top: body.style.top,
        width: body.style.width,
      };

      // ширина вертикального скроллбара
      const sbw = window.innerWidth - docEl.clientWidth;
      const scrollY = window.scrollY || docEl.scrollTop || 0;

      // фиксируем текущую позицию страницы
      body.style.overflow = "hidden";
      if (sbw > 0) body.style.paddingRight = `${sbw}px`;
      body.style.position = "fixed";
      body.style.top = `-${scrollY}px`;
      body.style.width = "100%";

    } else {
      // восстановление
      const prev = bodyLockRef.current;
      if (prev) {
        const y = Math.abs(parseInt(body.style.top || "0", 10)) || 0;
        body.style.overflow = prev.overflow || "";
        body.style.paddingRight = prev.paddingRight || "";
        body.style.position = prev.position || "";
        body.style.top = prev.top || "";
        body.style.width = prev.width || "";
        window.scrollTo(0, y); // вернём пользователя туда, где он был
        bodyLockRef.current = null;
      }
    }

    return () => {
      // на размонтирование — тоже аккуратно вернуть всё как было
      const prev = bodyLockRef.current;
      if (prev) {
        const y = Math.abs(parseInt(document.body.style.top || "0", 10)) || 0;
        document.body.style.overflow = prev.overflow || "";
        document.body.style.paddingRight = prev.paddingRight || "";
        document.body.style.position = prev.position || "";
        document.body.style.top = prev.top || "";
        document.body.style.width = prev.width || "";
        window.scrollTo(0, y);
        bodyLockRef.current = null;
      }
    };
  }, [isModalOpen]);




  // регистратор для сохранения
  const saveHandlers = useRef([]);
  const registerForSave = useCallback((product, api) => {
    // уникальность по product._id
    saveHandlers.current = saveHandlers.current.filter(
      (h) => h.product?._id !== product?._id
    );
    saveHandlers.current.push({ product, ...api });
  }, []);

  // Сохранение
  // где-то сверху файла, вне компонента:
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const handleSaveStocks = async () => {
    const token = localStorage.getItem("token");
    setIsSavingStocks(true);
    try {
      const batches = saveHandlers.current
        .map(({ product, getQuantities }) => {
          const current = getQuantities();
          const offerId = Number(product?.crm?.id);
          const sku = String(product?.crm?.sku || "");
          const warehouses = Array.isArray(product?.crm?.warehouse)
            ? product.crm.warehouse
            : [];

          const changed = warehouses
            .map((wh) => {
              const whId = Number(wh.id);
              const orig = Number(wh.quantity ?? 0);
              const raw = current[wh.id];
              const rawQty = raw && typeof raw === "object" ? raw.quantity : raw;
              const hasUserValue = rawQty !== "" && rawQty !== null && rawQty !== undefined;
              const cur = hasUserValue ? Number(rawQty) : orig;
              if (Number.isFinite(cur) && cur === orig) return null;
              if (!Number.isFinite(cur)) return null;
              return {
                warehouse_id: whId,
                stocks: [{ id: offerId, sku, quantity: cur }],
              };
            })
            .filter(Boolean);

          return { productId: product._id, batch: changed };
        })
        .filter((x) => x.batch.length > 0);

      if (batches.length === 0) {
        alert("Нет изменений для сохранения");
        setIsEditingStocks(false);
        return;
      }

      for (const { productId, batch } of batches) {
        const res = await fetch(
          `https://woh-crm.worldofheels.com.ua/v1/catalog/products/${productId}/stocks`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(batch),
          }
        );
        if (!res.ok) {
          const msg = await res.text().catch(() => "");
          const error = new Error(`PUT /products/${productId}/stocks failed: ${res.status} ${msg}`);
          // Сервер отвечает человеческим текстом, когда KeyCRM упёрлась в лимит
          // обращений: показать его пользователю полезнее, чем общее «ошибка».
          error.userMessage = readServerMessage(msg);
          throw error;
        }
        await sleep(120);
      }

      alert("Сохранено!");
      setIsEditingStocks(false);
    } catch (e) {
      console.error("Ошибка сохранения", e);
      alert(e?.userMessage || "Ошибка сохранения");
    } finally {
      setIsSavingStocks(false);
    }
  };



  // Отмена
  const handleCancelStocks = () => {
    saveHandlers.current.forEach(({ reset }) => reset?.());
    setIsEditingStocks(false);
  };

  // Склады
  useEffect(() => {
    fetchWarehouses(token).then((data) => setWarehouses(data || []));
  }, [token]);

  // === WebSocket: слушаем события синхронизации KeyCRM ===
  // === WebSocket: слушаем события синхронизации KeyCRM и обновления по вебхуку ===
  useEffect(() => {
    if (socketRef.current) return;

    const sock = io("https://woh-crm.worldofheels.com.ua", {
      path: "/v1/wss",
      transports: ["websocket"],
    });
    socketRef.current = sock;

    const triggerRefresh = () => {
      // добавляем ts в URL, чтобы форсировать useEffect загрузки
      const params = new URLSearchParams(location.search);
      params.set("ts", String(Date.now()));
      navigate(`?${params.toString()}`, { replace: true });
    };

    sock.on("connect", () => {
      // подписываемся на общую комнату от воркера
      sock.emit("join", { room: "keycrm" });
      console.log("✅ WS connected, joined room keycrm");
    });

    // --- Синхронизация (через воркер/RabbitMQ) ---
    sock.on("sync.keycrm.completed", (payload) => {
      console.log("🔁 Sync completed:", payload);
      triggerRefresh();
    });

    sock.on("sync.keycrm.failed", (payload) => {
      console.error("❌ Sync failed:", payload);
      // тут можно показать тост с ошибкой
    });

    // --- Вебхук обновил конкретный товар ---
    sock.on("stockUpdated", ({ product }) => {
      console.log("📦 Stock updated (room):", product);
      triggerRefresh();
    });

    // --- Глобальное обновление (если слушаем всё подряд)
    sock.on("stockUpdatedGlobal", (payload) => {
      const product = payload?.product;
      // сервер шлёт { updated, activeBatchId } без полного product -> просто обновляем список
      if (!product || product._id === undefined || product._id === null) { triggerRefresh(); return; }
      console.log("🌍 [stockUpdatedGlobal] received full product:", product);

      setProductsFromDB(prev =>
        prev.map(p =>
          String(p._id) === String(product._id)
            ? { ...p, ...product } // 👈 всегда новый объект
            : p
        )
      );


    });




    sock.onAny((event, ...args) => {
      console.log("📡 [SOCKET EVENT]", event, args);
    });



    return () => {
      sock.disconnect();
      socketRef.current = null;
    };
  }, [location.search, navigate]);



  // Пользователь
  useEffect(() => {
    async function fetchUser() {
      if (!token) return;
      const res = await fetch("https://woh-crm.worldofheels.com.ua/v1/iam/auth/me", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      }
    }
    fetchUser();
  }, [token]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const dynamicParams = {};
        queryParams.forEach((value, key) => {
          if (!["page", "limit", "q", "is_archived", "ts"].includes(key)) {
            dynamicParams[key] = value;
          }
        });

        const filters = {
          ...dynamicParams,
          q: queryParams.get("q") || searchQuery,
        };

        console.log("🔍 [fetchProducts] calling API with:", { page, limit, filters });


        const data = await getProducts(page, limit, filters, token);

        console.log("📥 [fetchProducts] API response:", data);

        const pages = Math.max(1, Number(data.pages || 1));
        if (page > pages) {
          const params = new URLSearchParams(location.search);
          params.set("page", "1");
          navigate(`?${params.toString()}`, { replace: true });
          return;
        }

        setFiltersLoading(data.filters || []);
        setProductsFromDB(data.items || []);
        setTotalPages(pages);
        setCurrentPage(page);
        setSearchQuery(queryParams.get("q") || "");

        console.log("✅ [fetchProducts] setProductsFromDB:", data.items);
      } catch (e) {
        console.error("❌ [fetchProducts] error:", e);
      }
    };

    fetchProducts();
  }, [location.search, token]);


  // фронтовая фильтрация архива
  const visibleProducts = productsFromDB.filter((p) =>
    showArchived ? true : !p.crm?.is_archived
  );

  // пагинация — скроллим вверх перед навигацией
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      scrollToTop(); // 👈 прокрутка вверх
      const params = new URLSearchParams(location.search);
      params.set("page", newPage);
      params.set("limit", limit);
      if (searchQuery) params.set("q", searchQuery);
      else params.delete("q");
      navigate(`?${params.toString()}`);
    }
  };

  // компактные номера страниц (адаптив)
  const renderPageNumbers = () => {
    const delta = isMobile ? 1 : 2;
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        pages.push(
          <button
            key={i}
            className={`page-btn ${i === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>
        );
      } else if (i === currentPage - delta - 1 || i === currentPage + delta + 1) {
        pages.push(
          <span key={`dots-${i}`} className="dots">
            …
          </span>
        );
      }
    }
    return pages;
  };

  // поиск — тоже уводим к началу списка
  const handleSearchChange = (query) => {
    setSearchQuery(query);
    scrollToTop(); // 👈 вверх
    const params = new URLSearchParams(location.search);
    params.set("page", 1);
    params.set("limit", limit);
    if (query) params.set("q", query);
    else params.delete("q");
    navigate(`?${params.toString()}`);
  };

  const searchValue = new URLSearchParams(location.search).get("q") || "";
  const warehouseFilterRaw = queryParams.getAll("warehouse_id")
    .flatMap((v) => v.split(",").map((s) => s.trim()).filter(Boolean));

  const activeInStockFilter = queryParams.get("in_stock") === "true";

  const activeWarehouseFilter = warehouseFilterRaw.map((id) => ({ value: id }));


  let visibleWarehouses = [...warehouses].sort((a, b) => a.crm.id - b.crm.id);

  if (activeWarehouseFilter.length > 0) {
    const allowedIds = activeWarehouseFilter.map((f) => String(f.value));
    visibleWarehouses = warehouses.filter((wh) =>
      allowedIds.includes(String(wh.crm.id))
    );
  }


  return (
    <div className="main-page">
      <Header
        onSearch={handleSearchChange}
        user={user}
        isEditingStocks={isEditingStocks}
        onEdit={() => setIsEditingStocks(true)}
        onSave={handleSaveStocks}
        onCancel={handleCancelStocks}
        isSavingStocks={isSavingStocks}
        searchValue={searchValue}
      />

      {isMobile && (
        <div className="mobile-block">
          <SearchBar className="search-bar" onSearch={handleSearchChange} searchValue={searchValue} />
          <div className="filter-reload">
            <OpenButton onClick={() => setModalOpen(true)} />
            <Reload />
          </div>
          <EditBalance
            isEditing={isEditingStocks}
            onEdit={() => setIsEditingStocks(true)}
            onSave={handleSaveStocks}
            onCancel={handleCancelStocks}
          />
        </div>
      )}

      <div className="container">
        {!isMobile && <FilterWrapper filtersLoading={filtersLoading} />}

        <div className="products-section">
          <div className="products-header">
            <div className="title-product">Товар/Название</div>
            <div className="title-warehouse">
              {!isMobile &&
                visibleWarehouses.map((warehouse) => (
                  <div className="warehouse-wrapper" key={warehouse.crm.id}>
                    <span>{warehouse.crm.name}</span>
                    <div className="subtitle-warehouse">
                      <p>Всего</p>
                      <p>Резерв</p>
                      <p>Доступно</p>
                    </div>
                  </div>
                ))}
              {isMobile && <span>Склад</span>}
              {isMobile && (
                <div className="subtitle-warehouse">
                  <p>Всего</p>
                  <p>Резерв</p>
                  <p>Доступно</p>
                </div>
              )}
            </div>
          </div>


          {visibleProducts.map((p) => (
            <ProductWrapper
              key={p?.crm?.id}
              product={p}
              user={user}
              isEditing={isEditingStocks}
              registerForSave={registerForSave}
              activeWarehouseFilter={activeWarehouseFilter}
              activeInStockFilter={activeInStockFilter}
            />
          ))}
        </div>

        <div className="pagination">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            className="page-btn"
          >
            ←
          </button>

          <button
            onClick={() => handlePageChange(Math.max(1, currentPage - 3))}
            disabled={currentPage <= 3}
            className="page-btn"
          >
            «
          </button>

          {renderPageNumbers()}

          <button
            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 3))}
            disabled={currentPage >= totalPages - 2}
            className="page-btn"
          >
            »
          </button>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className="page-btn"
          >
            →
          </button>
        </div>
      </div>

      {/* Плавающая кнопка «вверх/вниз» */}
      <button
        className="scroll-toggle"
        onClick={() => (atTop ? scrollToBottom() : scrollToTop())}
        aria-label={atTop ? "Прокрутить вниз" : "Прокрутить вверх"}
        title={atTop ? "Вниз" : "Вверх"}
      >
        {atTop ? "↓" : "↑"}
      </button>

      {isModalOpen && (
        <div className="modal-overlay" >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="filter-title">Фильтр поиска товаров</h3>
              <button className="modal-close" onClick={() => setModalOpen(false)}>
                ✕
              </button>
            </div>
            <FilterWrapper filtersLoading={filtersLoading} />
            <SearchButton setModalOpen={setModalOpen} />

          </div>
        </div>
      )}
      {isSavingStocks && (
        <div className="saving-overlay" role="status" aria-live="polite">
          <div className="saving-card">
            <div className="saving-spinner" aria-hidden="true" />
            <div className="saving-text">Сохраняем остатки…</div>
          </div>
        </div>
      )}

    </div>
  );
};

export default MainPage;
